import useQueryParams from "hooks/useQueryParams";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
// @material-ui/core components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
//MaterialUI
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMore";
import Pagination from "@material-ui/lab/Pagination";
import TableBackdrop from "components/Backdrops/TableBackdrop/TableBackdrop";
import limitaString from "utils/string_utils";

import CardTableContainer from "components/Cards/Tables/CardTableContainer";
import TableStateChip from "components/Cards/Tables/TableStateChip";
import { getMyOpportunityApplicationsAction } from "redux/actions/opportunitiesAction";
import { titles } from "resources/resources";
import { OPPORTUNITY_CANDIDATE_STATE_KEY, OPPORTUNITY_TYPE_KEY, PAGINATION, TABLE_STATE_CHIP_COLOR } from "utils/const";

import componentStylesGeneric from "assets/theme/views/admin/generic";

const useStylesGeneric = makeStyles(componentStylesGeneric);

function MyOpportunityApplicationsTab(props) {
  const theme = useTheme();
  const classes = useStylesGeneric();
  const { getQueryParam, setQueryParams, getQueryParams } = useQueryParams();
  const qpPrefix = useMemo(() => `myOppApps_${props.opportunityTypeKey ?? ""}_`, [props.opportunityTypeKey]);

  const [pageIndex, setPageIndex] = useState(() => {
    const fromUrl = getQueryParam(`${qpPrefix}pageIndex`);
    return fromUrl ? Number(fromUrl) : PAGINATION.FIRST_PAGE;
  });
  const [sortColumn, setSortColumn] = useState(() => getQueryParam(`${qpPrefix}sortColumn`) || null);
  const [sortDirection, setSortDirection] = useState(() => getQueryParam(`${qpPrefix}sortDirection`) || "asc");
  const intl = useIntl();

  const handleChangePage = (event, value) => {
    setPageIndex(value);
  };

  const tableHead = [
    { label: "Nº Candidatura", sortKey: "candidateCode" },
    { label: "Empresa", sortKey: "companyName" },
    { label: "Tipo de Oferta", sortKey: "opportunityTitle" },
    { label: "Data de Candidatura", sortKey: "creationDate" },
    { label: "Estado", sortKey: "candidateStateDescription" },
  ];

  const handleSort = (columnIndex) => {
    const column = tableHead[columnIndex].sortKey;
    
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPageIndex(PAGINATION.FIRST_PAGE);
  };

  // Sync state -> URL
  useEffect(() => {
    const current = getQueryParams();
    if (pageIndex && pageIndex > 1) current[`${qpPrefix}pageIndex`] = pageIndex;
    else delete current[`${qpPrefix}pageIndex`];

    if (sortColumn) current[`${qpPrefix}sortColumn`] = sortColumn;
    else delete current[`${qpPrefix}sortColumn`];

    if (sortDirection && sortDirection !== "asc") current[`${qpPrefix}sortDirection`] = sortDirection;
    else delete current[`${qpPrefix}sortDirection`];

    setQueryParams(current, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, sortColumn, sortDirection, qpPrefix]);

  // URL (back/forward) -> state
  useEffect(() => {
    const handlePopState = () => {
      const nextPageIndex = getQueryParam(`${qpPrefix}pageIndex`);
      const nextSortColumn = getQueryParam(`${qpPrefix}sortColumn`);
      const nextSortDirection = getQueryParam(`${qpPrefix}sortDirection`);

      setPageIndex(nextPageIndex ? Number(nextPageIndex) : PAGINATION.FIRST_PAGE);
      setSortColumn(nextSortColumn || null);
      setSortDirection(nextSortDirection || "asc");
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qpPrefix]);

  const renderSortIcon = (columnIndex) => {
    const column = tableHead[columnIndex].sortKey;
    if (sortColumn === column) {
      return sortDirection === "desc" ? (
        <ArrowUpwardIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle" }} />
      ) : (
        <ArrowDownwardIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle" }} />
      );
    }
    return <UnfoldMoreIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle", opacity: 0.3 }} />;
  };

  const getOpportunityTypeDescription = (opportunityTypeKey) => {
    switch (opportunityTypeKey) {
      case OPPORTUNITY_TYPE_KEY.ESTAGIO_PROFISSIONAL:
        return "Estágios Profissionais";
      case OPPORTUNITY_TYPE_KEY.EMPREGO:
        return "Emprego";
      default:
        return "";
    }
  };

  const mapOpportunityCandidateStateToTableChipColors = (stateKey) => {
    switch (stateKey) {
      case OPPORTUNITY_CANDIDATE_STATE_KEY.APROVADA:
        return TABLE_STATE_CHIP_COLOR.GREEN;
      case OPPORTUNITY_CANDIDATE_STATE_KEY.REJEITADA:
        return TABLE_STATE_CHIP_COLOR.RED;
      case OPPORTUNITY_CANDIDATE_STATE_KEY.EMRECRUTAMENTO:
        return TABLE_STATE_CHIP_COLOR.LIGHT_ORANGE;
      case OPPORTUNITY_CANDIDATE_STATE_KEY.DESISTENCIA:
        return TABLE_STATE_CHIP_COLOR.BLUE;
      default:
        return <></>;
    }
  };

  useEffect(() => {
    let filterData = {
      opportunityTypeKey: props.opportunityTypeKey,
      pageIndex: pageIndex,
      pageSize: PAGINATION.PAGE_SIZE,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
    };

    props.getMyOpportunityApplications(filterData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, props.opportunityTypeKey, sortColumn, sortDirection]);

  useEffect(() => {
    setPageIndex(PAGINATION.FIRST_PAGE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.opportunityTypeKey]);

  return (
    <>
      <CardTableContainer
        title={intl.formatMessage(titles.Title_Table_UserSingular_OpportunityApplications, {
          opportunityType: getOpportunityTypeDescription(props.opportunityTypeKey),
        })}
        count={props.opportunitiesState?.myOpportunityApplications_total_records}
      >
        <TableContainer>
          <Box component={Table} alignItems="center" marginBottom="0!important">
            <TableHead>
              <TableRow>
                {tableHead.map((prop, key) => (
                  <TableCell
                    key={key}
                    classes={{
                      root: classes.tableCellRoot + " " + classes.tableCellRootHead,
                    }}
                    onClick={() => handleSort(key)}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    <Box display="flex" alignItems="center">
                      {prop.label}
                      {renderSortIcon(key)}
                    </Box>
                  </TableCell>
                ))}
                <TableCell
                  key="detailsCol"
                  classes={{
                    root:
                      classes.tableCellRoot + " " + classes.tableCellRootHead + " " + classes.tableCellHeadStickyRight,
                  }}
                >
                  Detalhes
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props?.opportunitiesState?.myOpportunityApplications_result?.data?.map((prop, key) => (
                <TableRow key={key}>
                  <TableCell classes={{ root: classes.tableCellRoot }} title={prop.opportunityTitle ?? ""}>
                    <Box>{prop.candidateCode}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box fontWeight={"bold"}>NIF: {prop.companyTaxIdNumber}</Box>
                    <Box>{limitaString(prop.companyName, 60)}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box fontWeight={"bold"}>{prop.opportunityTypeDescription}</Box>
                    <Box>{limitaString(prop.opportunityTitle, 60)}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>{prop.creationDate}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <TableStateChip
                      state={mapOpportunityCandidateStateToTableChipColors(prop.candidateStateKey)}
                      message={prop.candidateStateDescription}
                    />
                  </TableCell>
                  <TableCell
                    classes={{
                      root: classes.tableCellRoot + " " + classes.tableCellBodyStickyRight,
                    }}
                  >
                    <Tooltip title="Detalhes">
                      <IconButton
                        aria-label="details"
                        component={Link}
                        to={`/admin/opportunity-application-details/${prop.candidateCode}`}
                      >
                        <Box
                          component={ArrowForwardIosIcon}
                          width="12px!important"
                          height="12px!important"
                          color={theme.palette.primary.main}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Box>
          <TableBackdrop open={props.opportunitiesState?.getMyOpportunityApplications_loading} />
        </TableContainer>

        <Pagination
          className={classes.alignPagination}
          count={props.opportunitiesState?.myOpportunityApplications_total_pages}
          page={pageIndex}
          onChange={handleChangePage}
          color="primary"
        ></Pagination>
      </CardTableContainer>
    </>
  );
}
const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getMyOpportunityApplications: (data) => dispatch(getMyOpportunityApplicationsAction(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyOpportunityApplicationsTab);
